import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, PanelTitle } from '../../components/layout/Card';
import { Avatar } from '../../components/data-display/Avatar';
import { Button } from '../../components/actions/Button';
import { FormField } from '../../components/forms/FormField';
import { TextField } from '../../components/forms/TextField';
import { useSession, tierLabel } from '../../lib/auth/useSession';
import { logout } from '../../services/authService';
import { getPushPermissionState, hasWebPushSupport } from '../../services/platformService';
import { openPushPermissionModal } from '../../services/pushPermissionModalService';
import { openPushBlockedModal } from '../../services/pushBlockedModalService';
import { AvatarDialog } from './AvatarDialog';
import { EmailDialog, PasswordDialog, PhoneDialog } from './ContactDialogs';
import { useUpdateName, useUpdateNotificationSettings } from './queries';
import { Switch } from '../../components/forms/Switch';
import { requestPushPermission } from '../../services/pushService';
import type { NotificationSettings } from '../../types/profileSettings';
import { notify } from '../../services/notify';

/**
 * The signed-in admin's own profile: photo, name, contact details, alert
 * notifications on this device, sign out. Admin users are onboarded
 * operationally, so there is no role or persona editing here.
 */
const nameSchema = z.object({
  firstName: z.string().trim().min(1, 'Enter a first name.'),
  lastName: z.string().trim().min(1, 'Enter a last name.'),
  displayName: z.string().trim().min(1, 'Enter a display name.'),
});

export function AccountPage() {
  const { profile } = useSession();
  const [dialog, setDialog] = useState<'avatar' | 'email' | 'phone' | 'password' | null>(null);
  if (!profile) return null;

  const name = profile.displayName || `${profile.firstName} ${profile.lastName}`.trim();

  return (
    <div className="grid max-w-[880px] gap-3.5">
      <Card>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => setDialog('avatar')}
            aria-label="Change profile photo"
            className="rounded-full border-0 bg-transparent p-0"
          >
            <Avatar src={profile.avatarUrl} name={name} size="xl" ring />
          </button>
          <div className="min-w-0 flex-1">
            <div className="truncate font-heading text-[19px] font-bold text-brown-deep">{name}</div>
            <div className="text-[13px] text-muted">
              {tierLabel(profile.roles)} · {profile.timezone || 'No timezone set'}
            </div>
          </div>
          <Button variant="ghost-dark" size="sm" onClick={() => setDialog('avatar')}>
            Change photo
          </Button>
        </div>
      </Card>

      <NameCard
        profileId={profile.id}
        firstName={profile.firstName}
        lastName={profile.lastName}
        displayName={profile.displayName}
      />

      <Card>
        <PanelTitle eyebrow="Contact">Email and phone</PanelTitle>
        <div className="divide-y divide-[rgba(143,68,32,.08)]">
          <ContactRow label="Email" value={profile.email} onEdit={() => setDialog('email')} />
          <ContactRow label="Phone" value={profile.phoneE164} onEdit={() => setDialog('phone')} />
        </div>
      </Card>

      <Card>
        <PanelTitle eyebrow="Sign-in">Password</PanelTitle>
        <p className="m-0 mb-4 text-sm text-muted">
          Set a password to sign in with your email and password instead of an emailed code.
        </p>
        <Button variant="ghost-dark" size="sm" onClick={() => setDialog('password')}>
          Set password
        </Button>
      </Card>

      <NotificationsCard profileId={profile.id} settings={profile.metadata?.settings?.notifications?.profile} />

      <Card>
        <PanelTitle eyebrow="Session">Sign out</PanelTitle>
        <p className="m-0 mb-4 text-sm text-muted">Signs you out of Mission Control on this device.</p>
        <Button variant="ghost-dark" size="sm" onClick={() => void logout()}>
          Sign out
        </Button>
      </Card>

      <AvatarDialog
        isOpen={dialog === 'avatar'}
        onClose={() => setDialog(null)}
        currentUrl={profile.avatarUrl}
        name={name}
      />
      {dialog === 'email' && (
        <EmailDialog onClose={() => setDialog(null)} profileId={profile.id} currentEmail={profile.email} />
      )}
      {dialog === 'phone' && <PhoneDialog onClose={() => setDialog(null)} currentPhone={profile.phoneE164} />}
      {dialog === 'password' && <PasswordDialog onClose={() => setDialog(null)} />}
    </div>
  );
}

function NameCard({
  profileId,
  ...current
}: {
  profileId: string;
  firstName: string;
  lastName: string;
  displayName: string;
}) {
  const update = useUpdateName(profileId);
  const form = useForm<z.infer<typeof nameSchema>>({ resolver: zodResolver(nameSchema), defaultValues: current });

  useEffect(() => {
    form.reset(current);
    // Reset only when the saved values change, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.firstName, current.lastName, current.displayName]);

  const submit = form.handleSubmit(async (values) => {
    await update.mutateAsync(values);
    notify.success('Name updated.');
  });

  const { errors, isDirty } = form.formState;
  return (
    <Card>
      <PanelTitle eyebrow="Profile">Name</PanelTitle>
      <form onSubmit={submit} noValidate className="grid gap-4 sm:grid-cols-3">
        <FormField label="First name" htmlFor="firstName" error={errors.firstName?.message}>
          <TextField
            id="firstName"
            autoComplete="given-name"
            invalid={!!errors.firstName}
            {...form.register('firstName')}
          />
        </FormField>
        <FormField label="Last name" htmlFor="lastName" error={errors.lastName?.message}>
          <TextField
            id="lastName"
            autoComplete="family-name"
            invalid={!!errors.lastName}
            {...form.register('lastName')}
          />
        </FormField>
        <FormField label="Display name" htmlFor="displayName" error={errors.displayName?.message}>
          <TextField id="displayName" invalid={!!errors.displayName} {...form.register('displayName')} />
        </FormField>
        <div className="sm:col-span-3">
          <Button type="submit" size="sm" disabled={!isDirty} loading={update.isPending}>
            Save
          </Button>
        </div>
      </form>
    </Card>
  );
}

function ContactRow({ label, value, onEdit }: { label: string; value?: string; onEdit: () => void }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3">
      <div className="min-w-0">
        <div className="text-[13px] font-semibold text-ink">{label}</div>
        <div className="truncate text-sm text-muted">{value || 'Not set'}</div>
      </div>
      <Button variant="ghost-dark" size="sm" onClick={onEdit}>
        {value ? 'Edit' : 'Add'}
      </Button>
    </div>
  );
}

type Channel = keyof NotificationSettings;

const CHANNELS: { key: Channel; label: string }[] = [
  { key: 'push', label: 'Push' },
  { key: 'email', label: 'Email' },
  { key: 'sms', label: 'SMS' },
];

/**
 * Channel preferences, ported from core's SettingsNotificationsPage.
 *
 * Push is special: persisting `true` is only honest if delivery is possible,
 * so it is persisted only after `requestPushPermission()` confirms browser
 * permission AND a registered token (directly when already granted, via the
 * permission modal's onGranted otherwise). Turning push off persists
 * immediately — the BE stops sending; the browser permission is untouched.
 */
function NotificationsCard({ profileId, settings }: { profileId: string; settings?: NotificationSettings }) {
  const update = useUpdateNotificationSettings(profileId);
  const pushDisabled = import.meta.env.VITE_FIREBASE_PUSH_DISABLED === 'true';
  const current: NotificationSettings = settings ?? { push: false, email: false, sms: false };

  const persist = (channel: Channel, value: boolean) => update.mutate({ ...current, [channel]: value });

  const toggle = async (channel: Channel, value: boolean) => {
    if (channel !== 'push' || !value) {
      persist(channel, value);
      return;
    }
    const perm = hasWebPushSupport() ? getPushPermissionState() : 'unsupported';
    if (perm === 'granted') {
      if (await requestPushPermission()) persist('push', true);
      return;
    }
    if (perm === 'default') {
      openPushPermissionModal({ onGranted: () => persist('push', true) });
      return;
    }
    if (perm === 'denied') {
      openPushBlockedModal();
      return;
    }
    // Unsupported here (e.g. iOS Safari tab): the modal shows install steps.
    openPushPermissionModal();
  };

  return (
    <Card>
      <PanelTitle eyebrow="Notifications">Alerts and notifications</PanelTitle>
      <p className="m-0 mb-2 text-sm text-muted">
        How alerts and notifications that name you reach you. They always appear under the activity bell too.
      </p>
      <div className="divide-y divide-[rgba(143,68,32,.08)]">
        {CHANNELS.map(({ key, label }) => {
          const disabled = update.isPending || (key === 'push' && pushDisabled);
          return (
            <div key={key} className="flex items-center justify-between gap-4 py-3">
              <div>
                <div className="text-[13px] font-semibold text-ink">{label}</div>
                {key === 'push' && (
                  <div className="text-xs text-muted">
                    {pushDisabled
                      ? 'Not configured for this environment.'
                      : 'On this device and any other signed-in device.'}
                  </div>
                )}
              </div>
              <Switch
                label={`${label} notifications`}
                checked={current[key] && !(key === 'push' && pushDisabled)}
                disabled={disabled}
                onChange={(value) => void toggle(key, value)}
              />
            </div>
          );
        })}
      </div>
    </Card>
  );
}
